import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const API_BASE = `${BASE_URL}/api/v1/preboarding`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const preBoardingDocumentService = {
  // ============================================================================
  // DOCUMENT UPLOAD ENDPOINTS
  // ============================================================================

  /**
   * Upload single document
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise}
   */
  uploadDocument: (formData) => {
    return api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Upload multiple documents
   * @param {FormData} formData - Form data with multiple files
   * @returns {Promise}
   */
  uploadMultipleDocuments: (formData) => {
    return api.post("/documents/upload-multiple", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Get documents for a candidate
   * @param {string} candidateId - Candidate ID
   * @param {Object} filters - Optional filters (status, category, documentType)
   * @returns {Promise}
   */
  getCandidateDocuments: (candidateId, filters = {}) => {
    return api.get(`/documents/candidate/${candidateId}`, { params: filters });
  },

  /**
   * Get document submission summary
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getSubmissionSummary: (candidateId) => {
    return api.get(`/documents/candidate/${candidateId}/summary`);
  },

  /**
   * Get pending documents for a candidate
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getPendingDocuments: (candidateId) => {
    return api.get(`/documents/candidate/${candidateId}/pending`);
  },

  /**
   * Get documents by category
   * @param {string} candidateId - Candidate ID
   * @param {string} category - Document category
   * @returns {Promise}
   */
  getDocumentsByCategory: (candidateId, category) => {
    return api.get(`/documents/candidate/${candidateId}/category/${category}`);
  },

  /**
   * Get document statistics
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getDocumentStatistics: (candidateId) => {
    return api.get(`/documents/candidate/${candidateId}/statistics`);
  },

  /**
   * Get single document details
   * @param {string} documentId - Document ID
   * @returns {Promise}
   */
  getDocument: (documentId) => {
    return api.get(`/documents/${documentId}`);
  },

  /**
   * Download document file
   * @param {string} documentId - Document ID
   * @returns {Promise<Blob>}
   */
  downloadDocument: (documentId) => {
    return api
      .get(`/documents/${documentId}/download`, {
        responseType: "blob",
      })
      .then((response) => response.data);
  },

  /**
   * Verify file integrity
   * @param {string} documentId - Document ID
   * @returns {Promise}
   */
  verifyFileIntegrity: (documentId) => {
    return api.get(`/documents/${documentId}/verify-integrity`);
  },

  /**
   * Update document metadata
   * @param {string} documentId - Document ID
   * @param {Object} updateData - Update data
   * @returns {Promise}
   */
  updateDocument: (documentId, updateData) => {
    return api.patch(`/documents/${documentId}`, updateData);
  },

  /**
   * Delete document (soft delete)
   * @param {string} documentId - Document ID
   * @returns {Promise}
   */
  deleteDocument: (documentId) => {
    return api.delete(`/documents/${documentId}`);
  },

  /**
   * Request document re-upload
   * @param {string} documentId - Document ID
   * @param {Object} data - { reason: string }
   * @returns {Promise}
   */
  requestReUpload: (documentId, data) => {
    return api.post(`/documents/${documentId}/request-reupload`, data);
  },

  /**
   * Get required documents by role
   * @param {string} role - Role (EMPLOYEE, CONTRACTOR, MANAGER)
   * @returns {Promise}
   */
  getRequiredDocuments: (role) => {
    return api.get(`/documents/requirements/${role}`);
  },

  /**
   * Get all documents for admin review (paginated)
   * @param {Object} params - Pagination params (page, limit, sortBy, sortOrder)
   * @returns {Promise}
   */
  getAllDocumentsForAdmin: (params = {}) => {
    return api.get("/documents/admin/all", { params });
  },

  /**
   * Get documents expiring soon
   * @param {number} daysThreshold - Days threshold (default 30)
   * @returns {Promise}
   */
  getExpiringDocuments: (daysThreshold = 30) => {
    return api.get("/documents/expiring/soon", {
      params: { daysThreshold },
    });
  },

  // ============================================================================
  // DOCUMENT VERIFICATION ENDPOINTS
  // ============================================================================

  /**
   * Assign document for review
   * @param {string} documentId - Document ID
   * @param {Object} reviewerData - { primaryReviewerId, secondaryReviewerId }
   * @returns {Promise}
   */
  assignDocumentForReview: (documentId, reviewerData) => {
    return api.post(`/verification/assign/${documentId}`, reviewerData);
  },

  /**
   * Start document review
   * @param {string} verificationId - Verification ID
   * @returns {Promise}
   */
  startReview: (verificationId) => {
    return api.patch(`/verification/${verificationId}/start-review`);
  },

  /**
   * Add comment to verification
   * @param {string} verificationId - Verification ID
   * @param {Object} commentData - { text, commentType }
   * @returns {Promise}
   */
  addComment: (verificationId, commentData) => {
    return api.post(`/verification/${verificationId}/comments`, commentData);
  },

  /**
   * Approve document
   * @param {string} verificationId - Verification ID
   * @param {Object} approvalData - { comments, verificationDetails }
   * @returns {Promise}
   */
  approveDocument: (verificationId, approvalData) => {
    return api.patch(`/verification/${verificationId}/approve`, approvalData);
  },

  /**
   * Reject document
   * @param {string} verificationId - Verification ID
   * @param {Object} rejectionData - { reason, reasonCodes }
   * @returns {Promise}
   */
  rejectDocument: (verificationId, rejectionData) => {
    return api.patch(`/verification/${verificationId}/reject`, rejectionData);
  },

  /**
   * Request re-upload (from verification)
   * @param {string} verificationId - Verification ID
   * @param {Object} reUploadData - { reason, deadline }
   * @returns {Promise}
   */
  requestVerificationReUpload: (verificationId, reUploadData) => {
    return api.patch(
      `/verification/${verificationId}/request-reupload`,
      reUploadData
    );
  },

  /**
   * Conditionally approve document
   * @param {string} verificationId - Verification ID
   * @param {Object} conditionalData - { conditions, comments }
   * @returns {Promise}
   */
  approveConditional: (verificationId, conditionalData) => {
    return api.patch(
      `/verification/${verificationId}/approve-conditional`,
      conditionalData
    );
  },

  /**
   * Get verification details
   * @param {string} verificationId - Verification ID
   * @returns {Promise}
   */
  getVerificationDetails: (verificationId) => {
    return api.get(`/verification/${verificationId}`);
  },

  /**
   * Get my pending reviews
   * @returns {Promise}
   */
  getMyPendingReviews: () => {
    return api.get("/verification/my-reviews");
  },

  /**
   * Get all verifications for a candidate
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getCandidateVerifications: (candidateId) => {
    return api.get(`/verification/candidate/${candidateId}`);
  },

  /**
   * Get verification summary for candidate
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getVerificationSummary: (candidateId) => {
    return api.get(`/verification/candidate/${candidateId}/summary`);
  },

  /**
   * Complete conditional approval
   * @param {string} verificationId - Verification ID
   * @param {number} conditionIndex - Condition index
   * @param {Object} completionData - { completionNotes }
   * @returns {Promise}
   */
  completeCondition: (verificationId, conditionIndex, completionData) => {
    return api.patch(
      `/verification/${verificationId}/complete-condition/${conditionIndex}`,
      completionData
    );
  },

  /**
   * Get verification statistics
   * @returns {Promise}
   */
  getVerificationStats: () => {
    return api.get("/verification/analytics/stats");
  },

  // ============================================================================
  // REMINDER ENDPOINTS
  // ============================================================================

  /**
   * Get candidate pending reminders
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getCandidateReminders: (candidateId) => {
    return api.get(`/reminders/candidate/${candidateId}`);
  },

  /**
   * Acknowledge reminder
   * @param {string} reminderId - Reminder ID
   * @returns {Promise}
   */
  acknowledgeReminder: (reminderId) => {
    return api.patch(`/reminders/${reminderId}/acknowledge`);
  },

  /**
   * Get escalated reminders (HR only)
   * @returns {Promise}
   */
  getEscalatedReminders: () => {
    return api.get("/reminders/escalated/all");
  },

  /**
   * Escalate reminder (HR only)
   * @param {string} reminderId - Reminder ID
   * @param {Object} data - { reason }
   * @returns {Promise}
   */
  escalateReminder: (reminderId, data) => {
    return api.patch(`/reminders/${reminderId}/escalate`, data);
  },

  // ============================================================================
  // CRON JOB ENDPOINTS (Admin only)
  // ============================================================================

  /**
   * Send pending reminders (cron job trigger)
   * @returns {Promise}
   */
  sendPendingReminders: () => {
    return api.post("/cron/send-reminders");
  },

  /**
   * Send overdue reminders (cron job trigger)
   * @returns {Promise}
   */
  sendOverdueReminders: () => {
    return api.post("/cron/send-overdue-reminders");
  },

  // ============================================================================
  // INTEGRATION ENDPOINTS
  // ============================================================================

  /**
   * Create initial reminder for accepted offer
   * @param {Object} data - { candidateId, expectedJoiningDate, channels }
   * @returns {Promise}
   */
  createInitialReminder: (data) => {
    return api.post("/integration/create-reminder", data);
  },

  // ============================================================================
  // ONBOARDING INTEGRATION (if needed)
  // ============================================================================

  /**
   * Get onboarding readiness report
   * @param {string} candidateId - Candidate ID
   * @returns {Promise}
   */
  getOnboardingReadiness: (candidateId) => {
    return api.get(`/integration/readiness/${candidateId}`);
  },

  /**
   * Complete onboarding
   * @param {Object} data - { candidateId, employeeId, joiningDate }
   * @returns {Promise}
   */
  completeOnboarding: (data) => {
    return api.post("/integration/complete-onboarding", data);
  },

  /**
   * Get batch onboarding summary
   * @param {Object} filters - Filter criteria
   * @returns {Promise}
   */
  getBatchOnboardingSummary: (filters = {}) => {
    return api.get("/integration/batch-summary", { params: filters });
  },
};

export default preBoardingDocumentService;
